// Used in teacher.component.html and home-page-container.component.html

// Angular
import {
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
} from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { Router } from "@angular/router";

// RXJS/Store
import * as coursesStore from "#core/teacher-classes/store";
import * as fromStore from "#core/teacher-classes/store";
import * as studentStore from "#student/student-shared/store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { AuthHelper } from "#helpers/auth.helper";
import * as fromLoginStore from "#login/store";
import { ViewAsStudentParams } from "#models/viewAsStudentParams.interface";
import { LocalStorage } from "#services/local-storage.service";
import { CoursesService } from "@carnegie-learning/data-access-angular-passport";
import { Course, PPUser as User } from "cli-types";

export interface DemoTeacherStudentToggleDialogData {
    courses: Course[];
    userProfile: User;
}

@Component({
    selector: "demo-teacher-student-toggle",
    templateUrl: "./demo-teacher-student-toggle.component.html",
    styleUrls: ["./demo-teacher-student-toggle.component.scss"],
    standalone: false
})
export class DemoTeacherStudentToggleComponent
    implements OnInit, OnChanges, OnDestroy
{
    teacherBtnClass: string;
    teacherBtnDisabled: boolean;
    studentBtnClass: string;
    studentBtnDisabled: boolean;
    courses: Course[];
    userData: User;
    displayToggle = false;
    private ngUnsubscribe = new Subject();

    @Input() viewMode: string;
    constructor(
        public dialog: MatDialog,
        private localStorage: LocalStorage,
        private course_store: Store<coursesStore.CoursesState>,
        private loginStore: Store<fromLoginStore.UserState>,
        private store_student: Store<studentStore.StudentCoursesState>,
        private coursesService: CoursesService,
        private authHelper: AuthHelper,
        private router: Router
    ) {}

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    ngOnInit(): void {
        this.setupButtons();

        if ((<any>window).isDemo) {
            this.displayToggle = true;

            this.course_store
                .pipe(select(fromStore.getAllCourses))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((courses: Course[]) => {
                    if (courses) {
                        this.courses = courses;

                        if (
                            this.courses.length === 1 &&
                            !this.courses[0].students
                        ) {
                            // edge case only a single class found need to load up all the classes
                            this.course_store
                                .pipe(
                                    select(fromStore.getCoursesPaginationParams)
                                )
                                .pipe(takeUntil(this.ngUnsubscribe))
                                .subscribe((params) => {
                                    const userData =
                                        this.localStorage.get("userProfile");
                                    if (params && userData) {
                                        // prevents from dispatching when logging out, this was triggered on logout
                                        this.course_store.dispatch(
                                            fromStore.loadCourses({
                                                payload: params,
                                            })
                                        );//TODO: Maybe something here too, for PAS-14357?
                                    }
                                });
                        }
                    }
                });

            this.userData = this.localStorage.get("userProfile");
        }
    }

    ngOnChanges(): void {
        this.setupButtons();
    }

    setupButtons() {
        if (this.viewMode === "teacher") {
            this.teacherBtnClass = "active";
            this.studentBtnClass = "inactive";
            this.teacherBtnDisabled = true;
            this.studentBtnDisabled = false;
        } else {
            // student mode
            this.teacherBtnClass = "inactive";
            this.studentBtnClass = "active";
            this.teacherBtnDisabled = false;
            this.studentBtnDisabled = true;
        }
    }

    click() {
        if (this.viewMode === "teacher") {
            this.openDialog();
        } else {
            // student mode
            this.onBackToTeacher();
            this.teacherBtnDisabled = true;
        }
    }

    openDialog(): void {
        const dialogRef = this.dialog.open(DemoTeacherStudentToggleDialog, {
            width: "456px",
            panelClass: "demo-toggle-dialogue-container",
            data: { courses: this.courses, userProfile: this.userData },
        });

        dialogRef.afterClosed().subscribe((courseID) => {
            if (courseID) {
                const courseObj = this.courses.find(
                    (obj) => obj.courseID === parseFloat(courseID)
                );
                if (
                    courseObj &&
                    courseObj.students &&
                    this.viewMode === "teacher"
                ) {
                    const viewAsStudentParams = {} as ViewAsStudentParams;
                    viewAsStudentParams.student = courseObj.students[0];
                    viewAsStudentParams.courseID = courseObj.courseID;
                    this.goToStudentView(viewAsStudentParams);
                }
            }
        });
    }

    goToStudentView(event: ViewAsStudentParams) {
        // dispatch action for SetViewAsStudentProfileData in login-form.action
        this.loginStore.dispatch(
            fromLoginStore.setViewAsStudentProfileData({ payload: event })
        );

        // route teacher to student side
        if ((<any>window).isDemo) {
            // On the demo server, we log in as the student using oauth.
            this.coursesService
                .loginAsStudent(event.student.userID, event.courseID)
                .subscribe((data) => {
                    this.authHelper.switchToStudentProfile(data);
                });
        } else {
            // In production we kick over the student side via the store.
            this.router.navigate([
                "/student/teacher-classes/" + event.courseID + "/assignments",
            ]);
        }
    }

    onBackToTeacher() {
        this.loginStore.dispatch(
            fromLoginStore.clearViewAsStudentProfileData()
        );
        if ((<any>window).isDemo) {
            // On the Demo server, we put the teacher's user profile in local storage.
            this.authHelper.restoreTeacherProfile();
            // After toggle back to teacher view, reset demo student selected teacher-class
            this.store_student.dispatch(
                studentStore.setSelectedCourse({ payload: null })
            );
        } else {
            // In production it's a store variable.
            this.router.navigate([`/teacher/classes`]);
        }
    }
    isArchivedCourses(): boolean {
        return !!this.courses.every((c) => c.archivedAt);
    }
}

@Component({
    selector: "demo-teacher-student-toggle-dialog",
    templateUrl: "demo-teacher-student-toggle-dialog.html",
    styleUrls: ["./demo-teacher-student-toggle.component.scss"],
    standalone: false
})
export class DemoTeacherStudentToggleDialog {
    bubbleSvg = "./assets/images/share/dual-bubbles.svg";
    uSvg = "./assets/images/share/orange-u.svg";
    selectedCourse: number;

    constructor(
        public dialogRef: MatDialogRef<DemoTeacherStudentToggleDialog>,
        @Inject(MAT_DIALOG_DATA) public data: DemoTeacherStudentToggleDialogData
    ) {}

    close(): void {
        this.dialogRef.close();
    }
    continue(): void {
        this.dialogRef.close(this.selectedCourse);
    }
}
